/**
 * Robot controller + unicycle kinematics. The player supplies only a desired
 * direction; the simulation turns it into a steering intent (after input
 * smoothing and steering assistance) and this module integrates the pose under
 * hard profile clamps, so the robot can never move in a physically impossible
 * way (product requirement 12). Speed is never negative under player control;
 * the automatic recovery maneuver (simulation.ts) may reverse briefly.
 */

import { clamp, wrapAngle } from "../core/vec";
import {
  BRAKE_STRENGTH,
  DT,
  INPUT_SMOOTHING_TAU,
  JOYSTICK_DEADZONE,
  PIVOT_RATE,
  STEER_DEADZONE,
  STEER_RATE_MAX,
  STEER_SPEED_MIN_SCALE,
  THROTTLE_DEADZONE,
  YAW_ACCEL,
} from "./constants";
import type { EffectiveProfile } from "./profile";
import type { JoystickCommand } from "./types";

export function isEngaged(cmd: JoystickCommand): boolean {
  return cmd.mag >= JOYSTICK_DEADZONE;
}

/** Resolved steering intent for one step (produced by the simulation). */
export interface ControlIntent {
  engaged: boolean;
  /** Assisted, clamped heading to steer toward (world yaw, rad from +Z). */
  heading: number;
  /** Control-frame yaw the command was clamped against (0 on lab levels). */
  frameYaw: number;
  /** Beyond-clamp command: rotate in place at zero target speed. */
  pivotOnly: boolean;
  /** Mission (ADR-022): pivot in place in this direction (+1 right / -1 left).
   * 0 = off. The pivot STOPS at `pivotTarget` (amendment M) so a pull-back is
   * one gesture — "turn me around" — not an endless spin the player must time. */
  pivotDir: 0 | 1 | -1;
  /** World heading the pivot rotates to and holds (null = rotate freely). */
  pivotTarget: number | null;
  /** Cap the yaw command by the braking curve sqrt(2 a |e|) (mission only). */
  yawBrake: boolean;
  /** Cruise target for this step (cruise or post-pass boost, m/s). */
  cruise: number;
  /** Corner-braking scale on the cruise target (1 = straight road). */
  cornerScale: number;
  /** Rate steering (ADR-023): the wheel position in [-1, 1], or null for the
   * heading model. When set, `heading`/`frameYaw`/`pivot*` are ignored. */
  steer: number | null;
  /** Rate steering: throttle/brake axis in [-1, 1] (negative = brake). */
  drive: number;
  /** Stick-magnitude throttle on the cruise target (1 = lab / full push). */
  throttle: number;
}

export interface RobotPose {
  x: number;
  z: number;
  heading: number;
  speed: number;
  yawRate: number;
}

/**
 * Advance the robot by one fixed timestep under an intent.
 * @param speedCap max speed allowed by the safety governor (m/s).
 */
/** The commanded Twist this step: what the controller asked the plant for. */
export interface CommandedTwist {
  /** Target forward speed after every cap (m/s) — the analogue of the real robot's joystick_vel_cmd.linear.x. */
  v: number;
  /** Desired yaw rate before rate limiting (rad/s) — the analogue of joystick_vel_cmd.angular.z. */
  w: number;
}

export function stepRobot(
  robot: RobotPose,
  intent: ControlIntent,
  speedCap: number,
  profile: EffectiveProfile,
  override?: CommandedTwist,
): CommandedTwist {
  let targetSpeed = 0;
  let desiredYawRate = 0;

  if (override) {
    // --- External driver (ADR-076) -----------------------------------------
    // A Twist from outside the joystick model — the real robot's own driver
    // running against the sim's cameras. It replaces only what the stick would
    // have decided (target speed and desired yaw rate); every plant limit
    // below still applies, as the robot's motion controller applies them to
    // /lane_follow_cmd. No reverse: the plant has none under player control
    // either. `w` is in the sim's convention, positive = turning right.
    targetSpeed = clamp(override.v, 0, profile.max_speed_mps);
    desiredYawRate = clamp(override.w, -profile.max_yaw_rate_radps, profile.max_yaw_rate_radps);
  } else if (intent.steer !== null) {
    // --- Racing model -----------------------------------------------------
    // Steering is a RATE relative to the robot: hold the wheel over and it
    // keeps turning. Speed-sensitive so it is not twitchy at cruise.
    const speedFrac = clamp(robot.speed / Math.max(0.1, profile.cruise_mps), 0, 1);
    const lock = 1 - (1 - STEER_SPEED_MIN_SCALE) * speedFrac;
    const wheel = Math.abs(intent.steer) < STEER_DEADZONE ? 0 : intent.steer;
    desiredYawRate = clamp(
      wheel * Math.min(STEER_RATE_MAX, profile.max_yaw_rate_radps) * lock,
      -profile.max_yaw_rate_radps,
      profile.max_yaw_rate_radps,
    );
    // Throttle forward, brake backward. The robot never reverses under player
    // control (product requirement); a held brake simply stops it.
    if (intent.drive > THROTTLE_DEADZONE) {
      targetSpeed = intent.cruise * intent.drive * intent.cornerScale;
    } else {
      targetSpeed = 0;
    }
  } else if (intent.engaged) {
    // Error measured INSIDE the control frame and deliberately unwrapped
    // there: both headings sit within ±MAX_COMMAND_HEADING of the frame, so
    // steering always passes through the front arc (a sign flip across
    // straight-down input never swings through ±180°) — on any road direction.
    if (intent.pivotDir !== 0) {
      // Rotate in place at a human pace; speed target zero. With a target, ease
      // into it (P control + braking curve) and hold there once reached, so the
      // robot cannot spin past the way out.
      const rate = Math.min(PIVOT_RATE, profile.max_yaw_rate_radps);
      if (intent.pivotTarget === null) {
        desiredYawRate = intent.pivotDir * rate;
      } else {
        // Signed error taken the way the pivot is turning, so it never
        // short-cuts backwards through the seam mid-turn.
        let err = wrapAngle(intent.pivotTarget - robot.heading);
        // Take the long way round only when there is a real turn to make:
        // forcing the stick's direction on a near-zero error would spin the
        // robot a full circle to reach the heading it is already on.
        const PIVOT_DIR_MIN = 0.25;
        if (Math.abs(err) > PIVOT_DIR_MIN) {
          if (intent.pivotDir > 0 && err < 0) err += 2 * Math.PI;
          if (intent.pivotDir < 0 && err > 0) err -= 2 * Math.PI;
        }
        const brake = Math.sqrt(2 * YAW_ACCEL * Math.abs(err));
        desiredYawRate = clamp(err * 3, -rate, rate);
        desiredYawRate = clamp(desiredYawRate, -brake, brake);
      }
      targetSpeed = 0;
    } else {
      const headingError =
        wrapAngle(intent.heading - intent.frameYaw) - wrapAngle(robot.heading - intent.frameYaw);
      // Proportional command; on the mission additionally capped by the
      // yaw-rate the bounded yaw deceleration can still bleed off before the
      // error closes (sqrt(2 a |e|)): no overshoot at high multipliers.
      let cmd = headingError * profile.steer_gain;
      if (intent.yawBrake) {
        const brakeCap = Math.sqrt(2 * YAW_ACCEL * Math.abs(headingError));
        cmd = clamp(cmd, -brakeCap, brakeCap);
      }
      desiredYawRate = clamp(cmd, -profile.max_yaw_rate_radps, profile.max_yaw_rate_radps);
      const alignment = intent.pivotOnly ? 0 : clamp(Math.cos(headingError), 0, 1);
      targetSpeed = intent.cruise * alignment * intent.cornerScale * intent.throttle;
    }
  }

  targetSpeed = Math.min(targetSpeed, speedCap);

  // Yaw-rate limiting: bounded rate and bounded rate-of-change.
  const yawDelta = clamp(desiredYawRate - robot.yawRate, -YAW_ACCEL * DT, YAW_ACCEL * DT);
  robot.yawRate = clamp(
    robot.yawRate + yawDelta,
    -profile.max_yaw_rate_radps,
    profile.max_yaw_rate_radps,
  );

  // Speed limiting: bounded accel/decel, never negative, never above max.
  const braking = intent.steer !== null && intent.drive < -THROTTLE_DEADZONE;
  const decel = profile.max_decel_mps2 * (braking ? BRAKE_STRENGTH : 1);
  const dv = clamp(targetSpeed - robot.speed, -decel * DT, profile.max_accel_mps2 * DT);
  robot.speed = clamp(robot.speed + dv, 0, profile.max_speed_mps);

  // Integrate pose. Stop rotating once fully stopped and released (no ghost spin).
  const spinning =
    (intent.steer !== null && Math.abs(intent.steer) >= STEER_DEADZONE) || override !== undefined;
  const effectiveYaw = robot.speed < 1e-4 && !intent.engaged && !spinning ? 0 : robot.yawRate;
  robot.heading = wrapAngle(robot.heading + effectiveYaw * DT);
  robot.x += robot.speed * Math.sin(robot.heading) * DT;
  robot.z += robot.speed * Math.cos(robot.heading) * DT;
  // Returned, not stored: the plant is unchanged, and callers that ignore
  // the value (every one before ADR-075) are unaffected.
  return { v: targetSpeed, w: desiredYawRate };
}

/** FIFO that delays joystick commands by control_latency_ms (whole sim steps). */
export class LatencyQueue {
  private readonly delaySteps: number;
  private readonly history: JoystickCommand[] = [];

  constructor(profile: { control_latency_ms: number }) {
    this.delaySteps = Math.max(0, Math.round(profile.control_latency_ms / 1000 / DT));
  }

  /** Push this step's raw command; returns the command the controller sees now. */
  push(raw: JoystickCommand): JoystickCommand {
    this.history.push(raw);
    const idx = this.history.length - 1 - this.delaySteps;
    if (idx < 0) return { dirX: 0, dirY: 0, mag: 0 };
    const cmd = this.history[idx] ?? { dirX: 0, dirY: 0, mag: 0 };
    // Trim the consumed prefix to keep memory bounded (after reading).
    if (idx > 600) this.history.splice(0, idx - 300);
    return cmd;
  }
}

/**
 * Exponential smoothing over the command direction, absorbing small accidental
 * joystick movements. Magnitude (and thus the deadzone) passes through
 * unsmoothed so engage/release stays crisp. Deterministic.
 */
export class InputSmoother {
  private sx = 0;
  private sy = 0;
  private active = false;
  private readonly tau: number;

  constructor(tau: number = INPUT_SMOOTHING_TAU) {
    this.tau = tau;
  }

  update(cmd: JoystickCommand): JoystickCommand {
    if (!isEngaged(cmd)) {
      this.active = false;
      this.sx = 0;
      this.sy = 0;
      return cmd;
    }
    if (!this.active) {
      // First engaged sample: adopt the direction directly (no lag on entry).
      this.active = true;
      this.sx = cmd.dirX;
      this.sy = cmd.dirY;
    } else {
      const alpha = 1 - Math.exp(-DT / this.tau);
      this.sx += (cmd.dirX - this.sx) * alpha;
      this.sy += (cmd.dirY - this.sy) * alpha;
    }
    const len = Math.hypot(this.sx, this.sy);
    if (len < 1e-6) return cmd;
    return { dirX: this.sx / len, dirY: this.sy / len, mag: cmd.mag };
  }
}
